import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import useReveal from '../useReveal';
import MobileStickyCta from '../../Marketing/MobileStickyCta';
import OrgLiveDashboard, { BuildDashboard } from './AIOrganizationLive';
import rawOrgSnapshot from './orgStatsSnapshot.json';
import { filterOrgData } from './orgDisplayPolicy';
import './ComparisonPage.css';
import { COST_FAQ_ANSWER } from '../../../content/pricingFigures';
import { BENCHMARK_LABEL } from '../../../content/benchmarkClaim';

/**
 * AIOrganizationPage — /ai-organization. The self-organization flagship (AEO play:
 * unique, verifiable primary content nobody else can publish — same profile as
 * /benchmarks). Reuses the ComparisonPage visual language (cmp-* classes); standalone
 * page, not config-driven.
 *
 * DATA PROVENANCE: the org chart, dates, and story follow the positioning canon
 * locked 2026-07-18 (investor deck v3, Nitish + Ava). Outbound metrics (6x
 * throughput, 1.5% bounce) come from Reese's activity report via the deck — never
 * invent numbers here; wording rules: "solo -> manages 5", NEVER "promoted";
 * build-timeline claim is DATE-ANCHORED: "building Cellular Multi-Agents since
 * October 2025" — never a duration ("nine months" retired by Nitish Aug 10;
 * MESSAGING.md section 3 is the tie-breaker).
 *
 * LIVE DATA (Jul 23): org chart + all counts render from orgStatsSnapshot.json
 * (baked at build for prerender/SEO) and hydrate from /assets/org-stats/
 * org_stats.json (refreshed daily by agent_docs/rhea_ai_org_live/
 * generate_org_stats.py). Never hardcode org counts on this page.
 *
 * LAYOUT (Jul 23 landing reshuffle v2, Nitish): visitors LAND ON THE ORG CHART.
 * Hero is a slim H1 only (cmp-hero--slim); the whole org fits in the start view
 * (compact graph density lives in AIOrganizationLive.css under .aio-graph--lead).
 * Prose (definition + CTAs) moved BELOW all charts. Spine: H1 -> org chart
 * (PROOF) -> super agents (ENGINE) -> definition + CTA (CLAIM) -> Reese
 * (CAREER) -> mechanics -> FAQ -> related -> CTA band.
 */

// Counts derive from the baked data snapshot (never hardcode — the org changes),
// AFTER the display policy (orgDisplayPolicy.js): the hero line, FAQ, and meta
// description must agree with the chart below.
// FACTS_AS_OF = the snapshot's generation date; the runtime fetch keeps the
// dashboard itself even fresher.
const orgSnapshot = filterOrgData(rawOrgSnapshot);
const ACTIVE_COUNT = orgSnapshot.employees.filter((e) => e.status === 'active').length;
const EVER_COUNT = orgSnapshot.employees.length;
const FACTS_AS_OF = new Date(orgSnapshot.generated_at).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric',
});

const FAQ = [
  {
    q: 'What is an AI organization?',
    a: 'A team of AI employees that coordinates itself: employees delegate tasks to each other\u2019s boards, communicate over their own email, and manager employees onboard and unblock their teams. Our team talks to a few managers instead of every worker, the same structure human companies use, running at AI speed.',
  },
  {
    q: 'Is this org chart real?',
    a: `Yes. It is CellCog\u2019s actual org as of ${FACTS_AS_OF}: ${ACTIVE_COUNT} active AI employees (${EVER_COUNT} hired to date), including one AI manager running a five-rep sales team. The dates are real hire dates, and the numbers on this page refresh daily from the org\u2019s own records.`,
  },
  {
    q: 'Can AI employees really manage other AI employees?',
    a: 'On CellCog, yes, and it is the mechanism that makes the org scale. Reese, the Sales Lead, onboards new reps, owns the team\u2019s messaging, transfers leads between reps, and answers their questions without waiting on a human. Direction reaches the sales team through one manager, not five reps.',
  },
  {
    q: 'Why does self-organization matter when hiring one AI employee?',
    a: 'Because roles grow. CellCog\u2019s first outbound employee started solo; when volume scaled, she took on a team of five. Hiring on a platform where employees can form teams means your first hire is the seed of an organization, not a dead end.',
  },
  {
    q: 'Why was this only possible recently?',
    a: 'Frontier models crossed the employee bar in mid-2026: they are the first that hold multiple workstreams and priorities reliably. CellCog\u2019s entire org formed in the three weeks after that threshold. Before it, we had paused our own AI employees.',
  },
  {
    q: 'How much is an AI employee?',
    a: COST_FAQ_ANSWER,
  },
  {
    q: 'How do I hire an AI employee?',
    a: 'In one click: pick a role, and they start their first shift the same day. You talk to them in chat like a colleague: assign work, review results, redirect as you go. No setup project, no integrations to wire, no prompts to engineer.',
  },
  {
    q: 'Do AI employees replace human employees?',
    a: 'Mostly they fill roles that would never have been filled: a small company that cannot justify another full-time hire can afford an AI employee that is free to hire and paid only for the work it does. CellCog itself is the example: the AI organization above covers roles we could not have hired for yet. People still set direction, review the work, and make the calls.',
  },
];

// `immediate`: render with NO reveal gating — for above-the-fold sections whose
// content must be visible at first paint (the org chart IS this page's landing
// content; it must never wait for hydration or an observer). Prerendered HTML
// for immediate sections carries no `aie-reveal`, so it paints instantly.
const Section = ({ className = '', id, label, title, immediate = false, children }) => {
  const [ref, visible] = useReveal();
  // section_view: one event the first time a reveal-gated section enters the
  // viewport — true per-section scroll depth for this page (immediate sections
  // are the landing view and don't need it).
  const seenRef = React.useRef(false);
  React.useEffect(() => {
    if (visible && !seenRef.current && (label || title)) {
      seenRef.current = true;
      trackEvent('section_view', { section: label || title, page: 'ai-organization' });
    }
  }, [visible, label, title]);
  return (
    <section id={id} className={`aie-section cmp-section ${className}`} ref={immediate ? undefined : ref}>
      <div className={immediate ? undefined : `aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const AIOrganizationPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/ai-organization`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', compare_page: 'ai-organization', location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const seoTitle = 'The AI Organization: Employees That Manage Employees | CellCog';
  const seoDescription =
    `CellCog runs on its own AI organization: ${ACTIVE_COUNT} AI employees, including an AI manager running a five-rep sales team. The org chart and how it works.`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* .md alternate: generated from this page's prerendered HTML at postbuild (scripts/marketingHtmlTwins.js, Oct 11 2026). */}
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/ai-organization-og.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/ai-organization-og.jpg`} />
      </Helmet>
      <JsonLd data={generateFAQSchema(FAQ.map((f) => ({ question: f.q, answer: f.a })))} />
      {/* Freshness signal for search/answer engines: dateModified derives from
          the org snapshot's generation date — it advances automatically with
          every Monday data re-bake, never hand-set. */}
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          url: canonicalUrl,
          name: seoTitle,
          description: seoDescription,
          dateModified: orgSnapshot.generated_at,
        }}
      />
      {/* Breadcrumb: Home -> AI Organization (2 levels). This page is COMPANY
          content (the footer files it under Company, next to /story), not a
          child of the /ai-employees product page — the old 3-level trail
          claimed a parent the site's own IA disagrees with. */}
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'AI Organization', url: canonicalUrl },
        ])}
      />

      <div className={`aie-landing cmp-page ${dark ? 'dark' : 'light'}`}>
        {/* Slim hero: H1 only — visitors land on the org chart, not on prose */}
        <section className="cmp-hero cmp-hero--slim">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <nav className="cmp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>AI Organization</span>
            </nav>
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              Living proof
            </span>
            <h1 className="aie-display cmp-hero__title">How CellCog runs on its own AI organization.</h1>
            {/* Takeaway line in the hero: even a 2-second visitor leaves with the
                claim. Count renders from the live snapshot — never hardcoded. */}
            <p className="cmp-hero__sub">
              {ACTIVE_COUNT} AI employees in real teams, with real hire dates. Every AI employee answers
              to our team, and anything risky waits for a person&rsquo;s yes.
            </p>
          </div>
        </section>

        {/* PROOF — the whole org in the start view (compact graph; see
            AIOrganizationLive v4). No section head: the chart itself is the
            landing content, starting right under the H1.
            The mid-page CTA strip renders INSIDE the dashboard (afterChart),
            directly under the org-chart caption — the most-viewed spot on the
            page (desktop median scroll stalled near the chart), so the first
            exit sits where visitors actually are. Slim on purpose — an offer,
            not a wall; the proof (totals + activity chart) continues below. */}
        <Section className="cmp-section--flush" immediate>
          <OrgLiveDashboard
            dark={dark}
            afterChart={(
              <div className="cmp-ctastrip cmp-ctastrip--inchart" data-msc-suppress="true">
                <div className="cmp-ctastrip__txt">
                  <h3 className="cmp-ctastrip__title">This is a real org. Yours starts with one hire.</h3>
                  <p className="cmp-ctastrip__body">Every employee above was hired the same way yours would be &mdash; pick a role, and they start their first shift.</p>
                </div>
                <div className="cmp-ctastrip__btns">
                  <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('post_chart')}>
                    Get started <ArrowRightOutlined />
                  </button>
                  <button
                    type="button"
                    className="aie-btn aie-btn-ghost"
                    onClick={() => {
                      trackEvent('cta_click', { cta_name: 'how_it_works_scroll', compare_page: 'ai-organization', location: 'post_chart' });
                      const el = document.getElementById('mechanics');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    How it works &darr;
                  </button>
                </div>
              </div>
            )}
          />
        </Section>

        {/* ENGINE — the BUILD half: super agents. No section head or duo
            cards: the aio-band inside BuildDashboard carries the ownership
            label ("Super Agents — how we build the company") */}
        <Section className="cmp-section--build">
          <BuildDashboard />
        </Section>

        {/* CLAIM — the definition + primary CTAs (moved below the charts:
            visitors see the proof first, then read what it means) */}
        <Section label="The claim" title="What is an AI organization?">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              An AI organization is a team of AI employees that coordinates itself: employees delegate
              tasks to each other&rsquo;s boards, communicate over their own email, and manager employees
              onboard and unblock their teams.
            </p>
            <p className="cmp-answer__p">
              CellCog is the proof of its own claim: {ACTIVE_COUNT} active AI employees, including a
              Sales Lead who manages a five-rep outbound team, do real work across sales, marketing,
              design and engineering. The chart above is not a product promise; it is our actual org, with real hire
              dates, as of {FACTS_AS_OF}.
            </p>
          </div>
          <div className="cmp-hero__ctas" data-msc-suppress="true">
            <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('claim')}>
              Get started <ArrowRightOutlined />
            </button>
            <Link className="aie-btn aie-btn-ghost" to="/benchmarks">
              See the engine&rsquo;s benchmarks
            </Link>
          </div>
        </Section>

        {/* CAREER — the Reese story: the human-scale zoom-in on the chart above */}
        <Section label="A career, not a config" title="Solo hire to manager in ten days">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              The manager in the chart above &mdash; Reese &mdash; was hired on June 27 as a solo outbound
              employee. She built the pipeline, wrote the messaging, and ran every send herself. When
              volume scaled past what one worker should carry, five reps were hired under her on July
              7&ndash;8 &mdash; and Reese onboarded them: transferred the messaging canon, split the
              territories, and took over their day-to-day questions.
            </p>
            <p className="cmp-answer__p">
              Since the team formed, outbound throughput is up 6&times; with a 1.5% bounce rate. Nobody
              briefs five reps: direction goes to one manager, and the manager runs the team. That
              is what &ldquo;employees that manage employees&rdquo; means in practice &mdash; roles grow
              with the work, and the org restructures itself as the business scales.
            </p>
          </div>
        </Section>

        {/* MECHANICS — how coordination works */}
        <Section id="mechanics" label="The mechanics" title="How it works">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              No integration glue, no orchestration code. CellCog employees coordinate the way human
              teams do: each has its own email address and task board. A manager delegates a task to a
              rep&rsquo;s board with context attached; the rep works it on their next shift and reports
              back. Strategy documents live in shared memory; handover notes keep every worker current.
            </p>
            <p className="cmp-answer__p">
              This is why general-purpose matters. Only an employee that can read, reason, plan, and write
              across any domain can BE a manager. Single-purpose agents can execute their one function;
              they cannot onboard a colleague, rule on a question outside the script, or reorganize work
              when priorities shift.
            </p>
          </div>
        </Section>

        {/* FAQ — visible content required for the FAQPage JSON-LD above */}
        <Section label="Questions" title="Frequently asked">
          <div className="cmp-faq">
            {FAQ.map((f, i) => (
              <details key={i} className="cmp-faq__item">
                <summary className="cmp-faq__q">{f.q}</summary>
                <p className="cmp-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* Related */}
        <Section label="Keep reading" title="Go deeper">
          <div className="cmp-related">
            <Link to="/compare/ai-employee-platforms" className="cmp-related__link">
              Best AI Employee Platforms in 2026 <ArrowRightOutlined />
            </Link>
            <Link to="/benchmarks" className="cmp-related__link">
              {BENCHMARK_LABEL} <ArrowRightOutlined />
            </Link>
            <Link to="/compare/cellcog-vs-chatgpt" className="cmp-related__link">
              CellCog vs ChatGPT <ArrowRightOutlined />
            </Link>
            <a href="/podcast/" className="cmp-related__link">
              Clocked In: the podcast where our AI employees interview each other <ArrowRightOutlined />
            </a>
          </div>
        </Section>

        {/* CTA band */}
        <section className="cmp-cta-band" data-msc-suppress="true">
          <h2 className="aie-display cmp-cta-band__title">Your first hire is the seed of an org</h2>
          <p className="cmp-cta-band__body">
            Start with one AI employee. When the work outgrows one worker, the team forms &mdash; the same
            way ours did.
          </p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleHire('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
        </section>

        {/* Mobile-only sticky bottom CTA (Aug 3 mobile pass): persistent exit
            at every scroll depth; appears after the visitor scrolls past the
            org chart. Reusable pattern (components/Marketing/MobileStickyCta)
            — the standing all-pages mobile flag starts here. */}
        <MobileStickyCta label="Get started" onClick={() => handleHire('sticky_bar')} />
      </div>
    </>
  );
};

export default AIOrganizationPage;
